// Módulo Negocios (§ 1.14, 2.2, 3.4): lista, kanban con drag&drop y ficha con
// líneas de producto (T-109/T-110) y barra de etapas.
import type { JSX } from "react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api.ts";
import { usePipelines, useAccountNames, type Stage } from "../lib/lookups.ts";
import { AsyncCombobox } from "../components/AsyncCombobox.tsx";
import type { Row } from "../lib/resource.ts";
import { formatDate, formatMoney } from "../lib/format.ts";
import { Button, Card, DotBadge, EmptyState, Field, Input, Loading, Modal, Select } from "../components/ui.tsx";
import { toast } from "../components/toast.tsx";

const LOSS_REASONS = ["Precio", "Competencia", "Sin presupuesto", "Sin respuesta", "Tiempo", "Otro"] as const;

function useDeals() {
  return useQuery<{ rows: Row[]; total: number }>({ queryKey: ["list", "deals"], queryFn: () => api.get("/deals") });
}

// ─────────────────────────── LISTA ───────────────────────────
export function DealList(): JSX.Element {
  const navigate = useNavigate();
  const deals = useDeals();
  const accountNames = useAccountNames();
  const { data: pipelines } = usePipelines();
  const [creating, setCreating] = useState(false);

  const stageName = useMemo(() => {
    const m = new Map<string, Stage>();
    for (const p of pipelines?.pipelines ?? []) for (const st of p.stages) m.set(st.id, st);
    return m;
  }, [pipelines]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-ink">Negocios</h1>
        <div className="flex gap-2">
          <Button onClick={() => navigate("/deals/kanban")}>Kanban</Button>
          <Button variant="primary" onClick={() => setCreating(true)}>+ Nuevo</Button>
        </div>
      </div>
      <Card>
        {deals.isLoading ? <Loading /> : (deals.data?.rows ?? []).length === 0 ? (
          <EmptyState title="Aún no hay negocios" body="Crea el primero para empezar tu pipeline."
            action={<Button variant="primary" onClick={() => setCreating(true)}>+ Nuevo</Button>} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr className="bg-canvas">
                <th className="th">Negocio</th><th className="th">Cuenta</th><th className="th">Etapa</th>
                <th className="th">Monto</th><th className="th">Cierre</th>
              </tr></thead>
              <tbody>
                {(deals.data?.rows ?? []).map((d) => {
                  const st = stageName.get(String(d["stageId"]));
                  return (
                    <tr key={String(d["id"])} className="hover:bg-surface-hover cursor-pointer"
                      onClick={() => navigate(`/deals/${String(d["id"])}`)}>
                      <td className="td font-medium text-primary">{String(d["dealName"])}</td>
                      <td className="td">{accountNames.get(String(d["accountId"])) ?? "—"}</td>
                      <td className="td">{st ? <DotBadge label={st.name} hex={st.color} /> : "—"}</td>
                      <td className="td">{formatMoney(d["amount"] as string)}</td>
                      <td className="td">{formatDate(d["closingDate"] as string)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {creating && <DealFormModal onClose={() => setCreating(false)} onCreated={(id) => { setCreating(false); navigate(`/deals/${id}`); }} />}
    </div>
  );
}

// ─────────────────────────── KANBAN ───────────────────────────
interface KanbanColumn { stageId: string; name: string; color: string; type: Stage["stageType"]; count: number; sum: string; cards: Row[]; }

export function DealKanban(): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: pipelines } = usePipelines();
  const [pipelineId, setPipelineId] = useState<string>("");
  const accountNames = useAccountNames();
  const [lossPrompt, setLossPrompt] = useState<{ dealId: string; stageId: string } | null>(null);

  const effectivePid = pipelineId || pipelines?.pipelines.find((p) => p.isDefault)?.id || pipelines?.pipelines[0]?.id || "";

  const kanban = useQuery<{ columns: KanbanColumn[] }>({
    queryKey: ["deals-kanban", effectivePid],
    queryFn: () => api.get(`/deals/kanban${effectivePid ? `?pipelineId=${effectivePid}` : ""}`),
    enabled: Boolean(pipelines),
  });

  const move = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Row }) => api.patch(`/deals/${id}`, body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["deals-kanban"] }); },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo mover el negocio."),
  });

  const onDrop = (dealId: string, col: KanbanColumn): void => {
    if (col.type === "closed_lost") { setLossPrompt({ dealId, stageId: col.stageId }); return; }
    if (col.type === "closed_won") {
      if (!window.confirm(`¿Marcar este negocio como GANADO?`)) return;
    }
    move.mutate({ id: dealId, body: { stageId: col.stageId } });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-ink">Negocios · Kanban</h1>
        <div className="flex gap-2 items-center">
          <Select value={effectivePid} onChange={(e) => setPipelineId(e.target.value)} className="w-52">
            {(pipelines?.pipelines ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
          <Button onClick={() => navigate("/deals")}>Lista</Button>
        </div>
      </div>
      {kanban.isLoading ? <Loading /> : (
        <div className="flex gap-3 overflow-x-auto pb-4">
          {(kanban.data?.columns ?? []).map((col) => (
            <div key={col.stageId} className="w-72 shrink-0"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { const id = e.dataTransfer.getData("text/plain"); if (id) onDrop(id, col); }}>
              <div className="flex items-center justify-between mb-2 px-1">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: col.color }} />
                  <span className="font-medium text-sm text-ink-secondary">{col.name}</span>
                </div>
                <span className="text-xs text-ink-muted">{col.count}</span>
              </div>
              <div className="bg-surface-hover rounded-lg p-2 space-y-2 min-h-24">
                {col.cards.map((c) => (
                  <div key={String(c["id"])} draggable
                    onDragStart={(e) => e.dataTransfer.setData("text/plain", String(c["id"]))}
                    onClick={() => navigate(`/deals/${String(c["id"])}`)}
                    className="card p-3 cursor-pointer hover:shadow-md">
                    <div className="font-medium text-sm text-ink">{String(c["dealName"])}</div>
                    <div className="text-xs text-ink-secondary">{accountNames.get(String(c["accountId"])) ?? ""}</div>
                    <div className="text-sm font-semibold text-ink-secondary mt-1">{formatMoney(c["amount"] as string)}</div>
                    <div className={`text-xs mt-0.5 ${isOverdue(c) ? "text-red-500" : "text-ink-muted"}`}>{formatDate(c["closingDate"] as string)}</div>
                  </div>
                ))}
                <div className="text-xs text-ink-secondary px-1 pt-1">{formatMoney(col.sum)}</div>
              </div>
            </div>
          ))}
        </div>
      )}
      {lossPrompt && (
        <LossReasonModal
          onClose={() => setLossPrompt(null)}
          onConfirm={(reason) => {
            move.mutate({ id: lossPrompt.dealId, body: { stageId: lossPrompt.stageId, lossReason: reason } });
            setLossPrompt(null);
          }}
        />
      )}
    </div>
  );
}

function isOverdue(card: Row): boolean {
  const cd = card["closingDate"];
  if (typeof cd !== "string") return false;
  return cd < new Date().toISOString().slice(0, 10);
}

function LossReasonModal({ onClose, onConfirm }: { onClose: () => void; onConfirm: (r: string) => void }): JSX.Element {
  const [reason, setReason] = useState<string>(LOSS_REASONS[0]);
  return (
    <Modal open onClose={onClose} title="Marcar como perdido"
      footer={<><Button onClick={onClose}>Cancelar</Button><Button variant="danger" onClick={() => onConfirm(reason)}>Marcar perdido</Button></>}>
      <Field label="Motivo de la pérdida" required>
        <Select value={reason} onChange={(e) => setReason(e.target.value)}>
          {LOSS_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
        </Select>
      </Field>
    </Modal>
  );
}

// ─────────────────────────── FORM ───────────────────────────
function DealFormModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }): JSX.Element {
  const qc = useQueryClient();
  const { data: pipelines } = usePipelines();
  const [dealName, setDealName] = useState("");
  const [accountId, setAccountId] = useState("");
  const [contactId, setContactId] = useState("");
  const [pipelineId, setPipelineId] = useState("");
  const [stageId, setStageId] = useState("");
  const [amount, setAmount] = useState("");
  const [closingDate, setClosingDate] = useState(new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10));
  const [err, setErr] = useState<Record<string, string>>({});

  const pipeline = (pipelines?.pipelines ?? []).find((p) => p.id === pipelineId) ?? pipelines?.pipelines.find((p) => p.isDefault) ?? pipelines?.pipelines[0];
  const openStages = (pipeline?.stages ?? []).filter((s) => s.stageType === "open");

  const create = useMutation({
    mutationFn: (body: Row) => api.post<{ deal: Row }>("/deals", body),
    onSuccess: (r) => { void qc.invalidateQueries({ queryKey: ["list", "deals"] }); toast.success("Negocio creado."); onCreated(String(r.deal["id"])); },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo crear el negocio."),
  });

  const submit = (): void => {
    const e: Record<string, string> = {};
    if (!dealName.trim()) e["dealName"] = "Este campo es obligatorio.";
    if (!accountId) e["accountId"] = "Este campo es obligatorio.";
    const pid = pipeline?.id;
    const sid = stageId || openStages[0]?.id;
    if (!pid || !sid) e["stageId"] = "Selecciona pipeline y etapa.";
    setErr(e);
    if (Object.keys(e).length > 0) return;
    const body: Row = { dealName, accountId, pipelineId: pid, stageId: sid, closingDate };
    if (contactId) body["contactId"] = contactId;
    if (amount) body["amount"] = Number(amount);
    create.mutate(body);
  };

  return (
    <Modal open onClose={onClose} title="Nuevo negocio" wide
      footer={<><Button onClick={onClose}>Cancelar</Button><Button variant="primary" onClick={submit} disabled={create.isPending}>{create.isPending ? "Guardando…" : "Guardar"}</Button></>}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <Field label="Nombre del negocio" required {...(err["dealName"] ? { error: err["dealName"] } : {})}>
            <Input value={dealName} onChange={(e) => setDealName(e.target.value)} />
          </Field>
        </div>
        <Field label="Cuenta" required {...(err["accountId"] ? { error: err["accountId"] } : {})}>
          <AsyncCombobox
            path="accounts"
            labelOf={(a) => String(a["accountName"] ?? "")}
            value={accountId}
            onChange={(id) => { setAccountId(id); setContactId(""); }}
            placeholder="Buscar cuenta…"
          />
        </Field>
        <Field label="Contacto" {...(accountId ? {} : { hint: "Selecciona una cuenta primero" })}>
          <AsyncCombobox
            path="contacts"
            {...(accountId ? { params: { accountId } } : {})}
            labelOf={(c) => `${String(c["firstName"] ?? "")} ${String(c["lastName"] ?? "")}`.trim()}
            value={contactId}
            onChange={(id) => setContactId(id)}
            disabled={!accountId}
            placeholder="Buscar contacto…"
          />
        </Field>
        <Field label="Pipeline">
          <Select value={pipeline?.id ?? ""} onChange={(e) => { setPipelineId(e.target.value); setStageId(""); }}>
            {(pipelines?.pipelines ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
        </Field>
        <Field label="Etapa" {...(err["stageId"] ? { error: err["stageId"] } : {})}>
          <Select value={stageId || openStages[0]?.id || ""} onChange={(e) => setStageId(e.target.value)}>
            {openStages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </Select>
        </Field>
        <Field label="Monto"><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} /></Field>
        <Field label="Fecha de cierre" required><Input type="date" value={closingDate} onChange={(e) => setClosingDate(e.target.value)} /></Field>
      </div>
    </Modal>
  );
}

// ─────────────────────────── DETALLE ───────────────────────────
export { DealDetail } from "./DealDetail.tsx";
